const RELOAD_GUARD_KEY = "healme-stale-chunk-reload"
const BUILD_VERSION_META = "build-version"

const isBrowser = (): boolean => typeof window !== "undefined"

const isPacksAssetUrl = (url: string): boolean => url.includes("/packs/")

const isStaleChunkMessage = (message: string): boolean =>
  message.includes("ChunkLoadError") ||
  message.includes("Loading chunk") ||
  message.includes("Failed to fetch dynamically imported module")

const readReloadGuard = (): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1"
  } catch {
    return false
  }
}

const markReloadAttempt = (): void => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1")
  } catch {
    // sessionStorage unavailable (private mode, iframe sandbox)
  }
}

const clearReloadGuard = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // ignore
  }
}

const showStaleVersionBanner = (): void => {
  if (document.getElementById("healme-stale-version-banner")) return

  const banner = document.createElement("div")
  banner.id = "healme-stale-version-banner"
  banner.setAttribute("role", "alert")
  banner.textContent = "A new version is available. Please refresh the page."
  banner.style.cssText =
    "position:fixed;bottom:0;left:0;right:0;padding:12px 16px;background:#1e3a5f;color:#fff;text-align:center;z-index:99999;font-size:14px;"
  document.body?.appendChild(banner)
}

const reloadForStaleAssets = (): void => {
  if (readReloadGuard()) {
    showStaleVersionBanner()
    return
  }

  markReloadAttempt()
  window.location.reload()
}

const installBuildVersionPolling = (): void => {
  const meta = document.querySelector(`meta[name="${BUILD_VERSION_META}"]`)
  const clientVersion = meta?.getAttribute("content")
  if (!clientVersion) return

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return

    fetch("/build_version", { cache: "no-store" })
      .then((response) => (response.ok ? response.text() : null))
      .then((serverVersion) => {
        if (serverVersion && serverVersion.trim() !== clientVersion.trim()) {
          reloadForStaleAssets()
        }
      })
      .catch(() => {
        // ignore transient network errors
      })
  })
}

export const installStaleChunkReload = (): void => {
  if (!isBrowser()) return

  window.addEventListener("load", () => {
    clearReloadGuard()
  })

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target
      if (!(target instanceof HTMLScriptElement || target instanceof HTMLLinkElement)) return

      const assetUrl =
        target instanceof HTMLScriptElement
          ? target.src
          : target instanceof HTMLLinkElement
            ? target.href
            : ""
      if (!assetUrl || !isPacksAssetUrl(assetUrl)) return

      reloadForStaleAssets()
    },
    true
  )

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason ?? "")
    if (!isStaleChunkMessage(message)) return

    reloadForStaleAssets()
  })

  installBuildVersionPolling()
}

installStaleChunkReload()
